import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { BENCHMARK_SINGLEAPPROVALQUOTE, DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import {
  updateSingleApprovalQuote,
  fetchApprovalQuote,
} from "../quotes/quotes.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";

// MUI v6 DatePicker imports
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { dateFormat } from "../../constants/app.constants";

type QuoteApprovalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  quoteId: number;
};

type UpdatedValue = string | Dayjs;

const QuoteApproveModal: FC<QuoteApprovalModalProps> = ({
  quoteId,
  isOpen,
  onClose,
}) => {
  const [approvedBy, setApprovedBY] = useState("");
  const [dateApproved, setDateApproved] = useState<Dayjs | null>(null);
  const [customerPO, setCustomerPO] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const { isFetching, data: dataBenchmarkSingleApprovalQuote, refetch } =
    useQuery({
      queryKey: [BENCHMARK_SINGLEAPPROVALQUOTE, quoteId],
      queryFn: () => fetchApprovalQuote(quoteId),
      enabled: isOpen,
      refetchOnWindowFocus: false,
    });

  // Populate fields when data arrives
  useEffect(() => {
    if (dataBenchmarkSingleApprovalQuote) {
      setApprovedBY(dataBenchmarkSingleApprovalQuote.ApprovedBy ?? "");
      setDateApproved(dataBenchmarkSingleApprovalQuote.DateApproved);
      setCustomerPO(dataBenchmarkSingleApprovalQuote.CustomerPO ?? "");
      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleApprovalQuote, quoteId]);

    const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "approvedBy") {
        setApprovedBY(updatedValue as string);
      } else if (id === "dateApproved") {
        setDateApproved(updatedValue as Dayjs);
      } else if (id === "customerPO") {
        setCustomerPO(updatedValue as string);
      }
    },
    []
  );

  const handleSubmit = async () => {
    if (!approvedBy.trim()) {
      setValidationMsg("Please enter who approved the quote.");
      return;
    }
    if (!dateApproved) {
      setValidationMsg("Please select the approval date.");
      return;
    }

    setValidationMsg("");
    setIsShowLoader(true);

    const result = await updateSingleApprovalQuote(
      quoteId,
      approvedBy,
      customerPO,
      dateApproved.toDate()
    );

    setIsShowLoader(false);
    if (result === "Success") {
      Swal.fire({
        customClass: { popup: "sweetAlert" },
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
        title: "Quote updated successfully!",
      });
      onClose();
    } else if (result === "mailnotsent") {
      Swal.fire({
        icon: "warning",
        title:
          "Quote approved but email failed. Please contact notifications@benchmarkcos.com",
      });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Failed to update quote.",
      });
    }
  };

  const handleCancel = () => {
    setApprovedBY("");
    setDateApproved(null);
    setCustomerPO("");
    setIsShowLoader(false);
    setValidationMsg("");
    onClose();
  };

  return (
    <Modal show={isOpen} label="Approval" onClose={handleCancel}>
      {validationMsg && (
        <div className="text-danger mb-2">{validationMsg}</div>
      )}

      <div className="quoteModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}

          <form noValidate>
            <div className="formControlGroup">
              <label className="formFieldLabel">
                Approved By<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <TextField
                  id="approvedBy"
                  onChange={onChangeFieldValues}
                  type="text"
                  defaultValue={approvedBy}
                />
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel">
                Date Approved<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select date"
                    value={dateApproved ? dayjs(dateApproved) : null}
                    onChange={(newValue) =>
                      onChangeFieldValues("dateApproved", newValue!)
                    }
                    format={DATE_FORMAT_UPDATED}
                    disableFuture
                    slotProps={{
                      textField: {
                        size: "small",
                        error: false,
                        helperText: "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel">Customer PO</label>
              <div className="formField">
                <TextField
                  id="customerPO"
                  onChange={onChangeFieldValues}
                  type="text"
                  defaultValue={customerPO}
                />
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel" />
              <div className="formField actionButtons">
                <button
                  type="button"
                  className="btn cancelPwdBtn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn submitBtn"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteApproveModal;
